/**
 * TextArea Component - Theme-aware textarea field
 * Extends Input component pattern for multi-line text input
 * ✅ Playbook Compliance: Theme-aware, WCAG 2.1 AA compliant, React.createElement
 */

const React = (globalThis as any).React;
const { useState, forwardRef } = React;

import { styles } from '../styles';

export interface TextAreaProps {
  value?: string;
  onChange?: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  error?: boolean;
  errorMessage?: string;
  required?: boolean;
  rows?: number;
  resize?: 'none' | 'vertical' | 'both' | 'horizontal';
  autoSize?: boolean | { minRows?: number; maxRows?: number };
  style?: React.CSSProperties;
  'aria-label'?: string;
  'aria-describedby'?: string;
  onKeyDown?: (e: React.KeyboardEvent) => void;
  id?: string;
  maxLength?: number;
  showCount?: boolean;
  children?: never; // TextArea is a void element
}

export const TextArea = (forwardRef as any)((props: TextAreaProps & { children?: never }, ref: React.Ref<HTMLTextAreaElement>) => {
  const {
    value,
    onChange,
    placeholder,
    disabled = false,
    error = false,
    errorMessage,
    required = false,
    rows = 4,
    resize = 'vertical',
    autoSize = false,
    style,
    'aria-label': ariaLabel,
    'aria-describedby': ariaDescribedBy,
    onKeyDown,
    id,
    maxLength,
    showCount = false,
    children: _children, // Explicitly ignore children
    ..._restProps
  } = props;

  const [isFocused, setIsFocused] = useState(false);
  const [currentRows, setCurrentRows] = useState(rows);

  // Calculate height for autoSize
  const calculateHeight = () => {
    if (!autoSize) return undefined;
    
    const minRows = typeof autoSize === 'object' ? (autoSize.minRows || rows) : rows;
    const maxRows = typeof autoSize === 'object' ? autoSize.maxRows : undefined;
    
    if (value) {
      const lineCount = value.split('\n').length;
      const calculatedRows = Math.max(minRows, maxRows ? Math.min(lineCount, maxRows) : lineCount);
      setCurrentRows(calculatedRows);
      return calculatedRows;
    }
    
    return minRows;
  };

  const textAreaRows = autoSize ? calculateHeight() : rows;

  const textAreaStyle: React.CSSProperties = {
    ...styles.input,
    ...(isFocused ? styles.inputFocus : {}),
    ...(error ? { borderColor: 'var(--error-color)' } : {}),
    ...(disabled ? { opacity: 0.5, cursor: 'not-allowed' } : {}),
    minHeight: `${textAreaRows ? textAreaRows * 20 : rows * 20}px`,
    resize: resize,
    fontFamily: 'monospace', // Common for code/configuration text
    lineHeight: '1.5',
    padding: '8px 12px',
    ...style,
  };

  const containerStyle: React.CSSProperties = {
    position: 'relative',
    width: '100%',
  };

  const textAreaProps: any = {
    ref,
    id: id || undefined,
    value: value || '',
    onChange: (e: any) => onChange?.(e.target.value),
    placeholder: placeholder || undefined,
    disabled: disabled || undefined,
    required: required || undefined,
    rows: textAreaRows || rows,
    style: textAreaStyle,
    onFocus: () => setIsFocused(true),
    onBlur: () => setIsFocused(false),
    onKeyDown: onKeyDown,
    maxLength: maxLength || undefined,
  };

  // Only add aria attributes if they have values
  if (ariaLabel) textAreaProps['aria-label'] = ariaLabel;
  if (errorMessage && id) textAreaProps['aria-describedby'] = `${id}-error`;
  else if (ariaDescribedBy) textAreaProps['aria-describedby'] = ariaDescribedBy;
  if (required) textAreaProps['aria-required'] = required;
  if (error) textAreaProps['aria-invalid'] = error;

  const characterCount = value ? value.length : 0;
  const countDisplay = maxLength ? `${characterCount}/${maxLength}` : characterCount.toString();

  return React.createElement('div', { style: containerStyle },
    React.createElement('textarea', textAreaProps),
    (errorMessage || (showCount && maxLength)) && React.createElement('div', {
      style: {
        display: 'flex',
        justifyContent: 'space-between',
        marginTop: '6px',
        fontSize: '13px',
      }
    },
      errorMessage && React.createElement('div', {
        id: `${id}-error`,
        role: 'alert',
        style: {
          color: 'var(--error-color)',
        },
      }, errorMessage),
      showCount && maxLength && React.createElement('div', {
        style: {
          color: characterCount > maxLength * 0.9 ? 'var(--warning-color)' : 'var(--main-text-secondary)',
          marginLeft: errorMessage ? '12px' : '0',
        },
      }, countDisplay)
    )
  );
});

